<template>
  <div class="p-5">
    <v-card class="p-5" flat>
      <v-row class="pb-0 pt-4">
        <!-- ستون سمت چپ: پیش‌نمایش -->
        <v-col cols="12" md="4" style="height: fit-content">
          <img
            v-if="qrSvgDataUrl"
            :src="qrSvgDataUrl"
            alt="QR Preview"
            class="cursor-pointer"
            width="100%"
            style="border-radius: 10px; max-height: 350px; object-fit: contain"
          />
          <v-skeleton-loader
            v-else
            type="image"
            height="350"
          ></v-skeleton-loader>
        </v-col>

        <!-- ستون سمت راست: تنظیمات -->
        <v-col cols="12" md="8">
          <div
            style="
              display: flex;
              flex-direction: row;
              justify-content: space-between;
              align-items: center;
            "
            class="mb-2"
            v-if="qrDesignMode === 'manual'"
          >
            <span style="direction: rtl; text-align: right">
              {{
                "لطفا پس از تنظیم دیزاین دستی، روی دکمه پیش نمایش کیوآرکد کلیک کنید"
              }}
            </span>
            <v-btn
              :loading="previewLoading"
              class="btn-get-code rounded-lg"
              depressed
              :disabled="
                (qrDesignMode === 'manual' && !hasChanges) || previewLoading
              "
              @click="handlePreviewClick"
            >
              پیش نمایش کیوآرکد <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </div>

          <v-col
            cols="12"
            style="height: fit-content; position: sticky; top: 0"
          >
            <!-- بخش اول: تنظیمات کارت NFC -->
            <v-expansion-panels
              v-model="activeNfcPanels"
              class="px-4 mb-5"
              flat
              accordion
            >
              <v-expansion-panel
                v-for="(item, i) in nfcPanels"
                :key="'nfc-' + i"
                class="mb-4"
              >
                <v-expansion-panel-header class="text-right">
                  <div class="d-flex justify-space-between align-center">
                    <div>
                      <v-icon class="me-2" color="primary"
                        >mdi-{{ item.icon }}</v-icon
                      >
                      <span style="font-weight: bold">{{ item.title }}</span>
                    </div>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="py-5">
                  <template v-if="item.name === 'BulkSettings'">
                    <bulk-settings-panel v-model="bulkOptions" />
                  </template>
                  <template v-else-if="item.name === 'BulkImageColor'">
                    <bulk-image-color-panel v-model="bulkOptions" />
                  </template>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-divider class="mx-4 mb-4"></v-divider>

            <!-- بخش دوم: انتخابگر نوع ساخت کیوآرکد -->
            <div class="px-4 mb-4">
              <p
                class="font-weight-bold mb-2 text-right"
                style="color: #001554"
              >
                <v-icon color="warning">mdi-qrcode-scan</v-icon>
                طراحی کیوآرکد
              </p>

              <v-radio-group
                v-model="qrDesignMode"
                row
                class="mt-0"
                style="direction: rtl"
              >
                <!-- با هر بار کلیک روی این گزینه، تابع رندوم اجرا می‌شود -->
                <v-radio
                  label="خودکار (طراحی تصادفی)"
                  value="auto"
                  color="primary"
                  @click="generateRandomDesign"
                ></v-radio>

                <v-radio
                  label="دستی (شخصی‌سازی رنگ و فرم)"
                  value="manual"
                  color="primary"
                ></v-radio>
              </v-radio-group>
            </div>
            <v-slide-y-transition>
              <div v-show="qrDesignMode === 'auto'">
                <v-btn
                  class="btn-get-code rounded-lg"
                  @click="generateRandomDesign"
                >
                  <v-icon small right></v-icon> ساخت خودکار کیوآرکد</v-btn
                >
              </div>
            </v-slide-y-transition>

            <!-- بخش سوم: تنظیمات کیوآرکد (فقط در حالت دستی پیداست) -->
            <v-slide-y-transition>
              <div v-show="qrDesignMode === 'manual'">
                <v-expansion-panels
                  v-model="activeQrPanels"
                  class="px-4"
                  flat
                  accordion
                >
                  <v-expansion-panel
                    v-for="(item, i) in qrPanels"
                    :key="'qr-' + i"
                    class="mb-4"
                  >
                    <v-expansion-panel-header class="text-right">
                      <div class="d-flex justify-space-between align-center">
                        <div>
                          <v-icon class="me-2 text-muted"
                            >mdi-{{ item.icon }}</v-icon
                          >
                          {{ item.title }}
                        </div>
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="py-5">
                      <component
                        :is="item.name"
                        v-model="options"
                        :colors="colors"
                        :readonly="false"
                        :design-object="getDesignObject(item.name)"
                      />
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>
            </v-slide-y-transition>
          </v-col>

          <!-- دکمه ساخت نهایی -->
          <div class="py-5 mt-0 col-md-12">
            <div class="rowinfo mt-0 mr-auto float-left justify-center">
              <v-btn
                :disabled="
                  isGenerating ||
                  bulkOptions.count < 1 ||
                  bulkOptions.count > 200
                "
                :loading="isGenerating"
                :class="
                  generationSuccess
                    ? 'btn-download rounded-lg'
                    : 'btn-get-code rounded-lg'
                "
                @click="generateBulkFiles"
              >
                <v-icon small class="mr-2">{{
                  isGenerating ? "mdi-refresh" : "mdi-qrcode"
                }}</v-icon>
                {{ isGenerating ? "در حال ساخت..." : "ساخت گروهی کیوآرکدها" }}
              </v-btn>
            </div>
          </div>
        </v-col>
      </v-row>

      <v-divider class="my-6"></v-divider>
      <nfc-file-manager ref="fileManagerComponent" />
    </v-card>
  </div>
</template>

<script>
import QrCodeDataAdv from "@/view/pages/qrland/qrCodeStyle/components/qrCodeDataAdv";
import QrCodeDotsAdv from "@/view/pages/qrland/qrCodeStyle/components/qrCodeDotsAdv";
import QrCodeEyeAdv from "@/view/pages/qrland/qrCodeStyle/components/qrCodeEyeAdv";
import QrCodeOptions from "@/view/pages/qrland/qrCodeStyle/components/qrCodeOptions";
import QrCodeSquareAdv from "@/view/pages/qrland/qrCodeStyle/components/qrCodeSquareAdv";
import QrCodeFrameAdv from "@/view/pages/qrland/qrCodeStyle/components/qrCodeFrameAdv";
import QrCodeLogoAdv from "@/view/pages/qrland/qrCodeStyle/components/qrCodeLogoAdv";
import BulkSettingsPanel from "./BulkSettingsPanel";
import BulkImageColorPanel from "./BulkImageColorPanel";
import NfcFileManager from "./NfcFileManager.vue";

export default {
  name: "NfcQrGenerator",

  components: {
    QrCodeDataAdv,
    QrCodeDotsAdv,
    QrCodeEyeAdv,
    QrCodeOptions,
    QrCodeSquareAdv,
    QrCodeFrameAdv,
    QrCodeLogoAdv,
    BulkSettingsPanel,
    BulkImageColorPanel,
    NfcFileManager,

  },

  data() {
    return {
      qrDesignMode: "auto",
      // ('auto' یا 'manual')
      activeNfcPanels: [0],
      activeQrPanels: [],
      latestGeneratedFilePath: "",
      generationSuccess: false,
      activePanels: [0],
      hasChanges: false,
      previewLoading: false,
      isGenerating: false,
      isFetchingFiles: false,
      qrSvgDataUrl: "",
      explorerPath: "",

      designObject_dots: [],
      designObject_patttern: [],
      designObject_square: [],
      designObject_frames: [], 
      designObject_logos: [],


      count: 1,
      toggle_exclusive: 4,
      selectFromGroup: false,

      bulkOptions: {
      outer_frame: "bottom",
      framelabel: "مرا اسکن کنید",
      label_font: "Parastoo.ttf",
      "label-text-size": 100,
      framecolor: "#1754ec",
      custom_frame_color: "on",

      optionlogo: "02-email-b.png",
      no_logo_bg: "on",
      logo_size: 100,

        id: 1,
        transparent: "on",
        count: 20,
        title: "کارت nfc",
        status: "unavailable", // unavailable,available,complete,assigned
        orientation: "horizontal", // horizontal,vertical
        front_image: "",
        back_image: "",
        bgType: "solid",
        solidColor: "#000000",
        gradientColor1: "#000000",
        gradientColor2: "#000000",
        gradientDirection: "to right",
      },

      options: {
        outer_frame: "bottom",
        framelabel: "مرا اسکن کنید",
        label_font: "Parastoo.ttf",
        "label-text-size": 100,
        framecolor: "#1754ec",
        custom_frame_color: "on",

        optionlogo: "02-email-b.png",
        no_logo_bg: "on",
        logo_size: 100,
        different_borders_color: false,
        different_eyes_color: false,
        marker_out_color: "#000000",
        marker_in_color: "#000000",
        marker_top_right_center: "#000000",
        marker_top_right_outline: "#000000",
        marker_bottom_left_outline: "#000000",
        marker_bottom_left_center: "#000000",
        different_markers_color: false,
        width: 300,
        height: 300,
        data: "https://qr2.in",
        margin: 5,
        qrOptions: {
          typeNumber: 0,
          mode: "Byte",
          errorCorrectionLevel: "H",
        },
        imageOptions: { hideBackgroundDots: true, imageSize: 0.4, margin: 0 },
        dotsOptions: {
          type: "extra-rounded",
          color: "#000000",
          gradient: null,
        },
        backgroundOptions: { color: "rgba(255, 255, 255, 0)", gradient: null },
        image: null,
        cornersSquareOptions: {
          type: "extra-rounded",
          gradient: null,
          color: "#000000",
        },
        cornersDotOptions: { type: "", color: "#000000", gradient: null },
      },

      colors: [
        ["#649173", "#DBD5A4"],
        ["#B993D6", "#8CA6DB"],
        ["#870000", "#190A05"],
        ["#00d2ff", "#3a7bd5"],
        ["#DAD299", "#B0DAB9"],
        ["#E6DADA", "#274046"],
        ["#50C9C3", "#96DEDA"],
        ["#4b6cb7", "#182848"],
        ["#EC6F66", "#F3A183"],
        ["#5f2c82", "#49a09d"],
      ],
      nfcPanels: [
        {
          name: "BulkSettings",
          title: "تنظیمات کارت NFC",
          icon: "card-account-details-outline",
        },
        {
          name: "BulkImageColor",
          title: "تصاویر و پس‌زمینه کارت NFC",
          icon: "image-multiple-outline",
        },
      ],
      qrPanels: [
        {
          name: "QrCodeDotsAdv",
          title: "طراحی نقاط کیوآرکد",
          icon: "dots-triangle",
        },
        {
          name: "QrCodeEyeAdv",
          title: "طراحی چشم داخلی کیوآرکد",
          icon: "bullseye",
        },
        {
          name: "QrCodeSquareAdv",
          title: "طراحی حاشیه کیوآرکد",
          icon: "rounded-corner",
        },
        { name: "QrCodeFrameAdv", title: "طراحی قاب", icon: "border-all" }, 
        { name: "QrCodeLogoAdv", title: "تنظیمات لوگو", icon: "image" }, 

        {
          name: "QrCodeOptions",
          title: "تنظیمات پیشرفته کیوآرکد",
          icon: "cogs",
        },
      ],
      tableHeaders: [
        { text: "ردیف", value: "index", align: "center", sortable: false },
        { text: "نام فایل", value: "name", align: "center" },
        { text: "عملیات", value: "actions", align: "center", sortable: false },
      ],
    };
  },

  watch: {
    qrDesignMode(newVal) {
      if (newVal === "auto") {
        this.options.dotsOptions.type = "extra-rounded";
        this.options.dotsOptions.color = "#000000";
        this.options.cornersSquareOptions.type = "extra-rounded";
        this.options.cornersSquareOptions.color = "#000000";
        this.options.cornersDotOptions.type = "";
        this.options.cornersDotOptions.color = "#000000";

        this.updatePreview();
      }
    },
    options: {
      handler() {
        this.hasChanges = true;
        this.generationSuccess = false;
        this.latestGeneratedFilePath = "";
      },
      deep: true,
    },
    bulkOptions: {
      handler() {
        this.hasChanges = true;
        this.generationSuccess = false;
        this.latestGeneratedFilePath = "";
      },
      deep: true,
    },
  },

  mounted() {
    this.fetchDesigns();
    this.generateRandomDesign();
    // this.fetchFiles();
    setTimeout(() => {
      this.updatePreview();
    }, 500);
  },

  methods: {
    cleanPayload(obj) {
      const cleaned = {};
      Object.keys(obj).forEach((key) => {
        if (obj[key] !== null && obj[key] !== undefined) {
          cleaned[key] = obj[key];
        }
      });
      return cleaned;
    },

    isGradient(opt) {
      let res = {
        color: "",
        color1: "",
        isGradient: false,
      };
      if (opt && opt.hasOwnProperty("gradient") && !!opt.gradient) {
        res = {
          isGradient: true,
          color: opt.gradient.colorStops[0].color,
          color1: opt.gradient.colorStops[1].color,
        };
      } else if (opt) {
        res = {
          isGradient: false,
          color: opt.color,
          color1: null,
        };
      }
      return res;
    },
    handlePreviewClick() {
      if (this.qrDesignMode === "auto") {
        this.generateRandomDesign();
      } else {
        this.updatePreview();
      }
    },
    generateRandomDesign(isInitial = false) {
      this.qrDesignMode = "auto";

      const getRandomItem = (arr) => {
        if (!arr || arr.length === 0) return null;
        const randomIndex = Math.floor(Math.random() * arr.length);
        return arr[randomIndex].value;
      };

      const getRandomDarkColor = () => {
        return `#${Math.floor(Math.random() * 150)
          .toString(16)
          .padStart(2, "0")}${Math.floor(Math.random() * 150)
          .toString(16)
          .padStart(2, "0")}${Math.floor(Math.random() * 150)
          .toString(16)
          .padStart(2, "0")}`;
      };

      this.options.dotsOptions.type =
        getRandomItem(this.designObject_patttern) || "rounded";
      this.options.cornersSquareOptions.type =
        getRandomItem(this.designObject_square) || "extra-rounded";
      this.options.cornersDotOptions.type =
        getRandomItem(this.designObject_dots) || "dot";

      if (isInitial === true || true) {
        // --- حالت اولیه: کاملا سیاه ---
        this.options.different_borders_color = false;
        this.options.different_eyes_color = false;

        const black = "#000000";
        this.options.dotsOptions.color = black;
        this.options.cornersSquareOptions.color = black;
        this.options.cornersDotOptions.color = black;

        this.options.marker_out_color = black;
        this.options.marker_in_color = black;
        this.options.marker_top_right_outline = black;
        this.options.marker_top_right_center = black;
        this.options.marker_bottom_left_outline = black;
        this.options.marker_bottom_left_center = black;
      } else {
        // --- حالت تصادفی: کاملا رنگی ---
        this.options.different_borders_color = true;
        this.options.different_eyes_color = true;

        this.options.dotsOptions.color = getRandomDarkColor();
        this.options.marker_out_color = getRandomDarkColor();
        this.options.marker_in_color = getRandomDarkColor();
        this.options.marker_top_right_outline = getRandomDarkColor();
        this.options.marker_top_right_center = getRandomDarkColor();
        this.options.marker_bottom_left_outline = getRandomDarkColor();
        this.options.marker_bottom_left_center = getRandomDarkColor();
      }

      this.updatePreview();
    },
    getDesignObject(name) {

      if (name === "QrCodeDotsAdv") return this.designObject_patttern;
      if (name === "QrCodeEyeAdv") return this.designObject_dots;
      if (name === "QrCodeSquareAdv") return this.designObject_square;
      if (name === 'QrCodeFrameAdv')  return this.designObject_frames;
      if (name === 'QrCodeLogoAdv') return this.designObject_logos;

      return [];
    },

    fetchDesigns() {
  this.$api.get("/v2/qr-gen/markers")
    .then(({ data }) => {
      const apiData = data.data;
      
      this.designObject_patttern = Object.keys(apiData.pattern || {}).map((key) => ({ value: key, img: apiData.pattern[key] }));
      this.designObject_dots = Object.keys(apiData.markerIn || {}).map((key) => ({ value: key, img: apiData.markerIn[key] }));
      this.designObject_square = Object.keys(apiData.markerOut || {}).map((key) => ({ value: key, img: apiData.markerOut[key] }));
      
      this.designObject_frames = Object.keys(apiData.frames || {}).map((key) => ({ 
        value: key, 
        data: apiData.frames[key] 
      }));
      
      this.designObject_logos = Object.keys(apiData.logos || {}).map((key) => ({ 
        value: key, 
        img: apiData.logos[key].preview 
      }));
    })
    .catch(() => {});
},

    getQrDesignParams() {
      const opt = this.options;

      let backgroundColor = this.isGradient(opt.backgroundOptions);
      let frontColor = this.isGradient(opt.dotsOptions);
      let cornerSquare = this.isGradient(opt.cornersSquareOptions);
      let cornerDots = this.isGradient(opt.cornersDotOptions);

      return {
        // --- پارامترهای فرم اصلی ---
        transparent: this.bulkOptions.transparent || "off",
        level: opt.qrOptions.errorCorrectionLevel || "H",
        pattern: opt.dotsOptions.type || "ellipse",
        marker_out: opt.cornersSquareOptions.type || "sdoz",
        marker_in: opt.cornersDotOptions.type || "sdoz",

        // --- تنظیمات رنگ اصلی (نقاط و پس‌زمینه) ---
        backcolor: backgroundColor.color,
        color: frontColor.color,
        radial: false,
        gradient: frontColor.isGradient,
        gradient_color: frontColor.color1,

        // --- تنظیمات رنگ حاشیه‌ها (Borders / Marker Out) ---
        marker_out_color: opt.different_borders_color
          ? opt.marker_out_color
          : cornerSquare.color,
        marker_top_right_outline: opt.different_borders_color
          ? opt.marker_top_right_outline
          : cornerSquare.color,
        marker_bottom_left_outline: opt.different_borders_color
          ? opt.marker_bottom_left_outline
          : cornerSquare.color,

        // --- تنظیمات رنگ چشم‌ها (Eyes / Marker In) ---
        marker_in_color: opt.different_eyes_color
          ? opt.marker_in_color
          : cornerDots.color,
        marker_top_right_center: opt.different_eyes_color
          ? opt.marker_top_right_center
          : cornerDots.color,
        marker_bottom_left_center: opt.different_eyes_color
          ? opt.marker_bottom_left_center
          : cornerDots.color,

        // --- متغیرهای کنترل‌کننده ---
        different_markers_color:
          opt.different_borders_color || opt.different_eyes_color,
        markers_color: true,

        outer_frame: opt.outer_frame,
        framelabel: opt.framelabel,
        label_font: opt.label_font,
        "label-text-size": opt["label-text-size"],
        framecolor: opt.custom_frame_color === 'on' ? opt.framecolor : '',
        custom_frame_color: opt.custom_frame_color,
        
        optionlogo: opt.optionlogo,
        no_logo_bg: opt.no_logo_bg,
        logo_size: opt.logo_size,

      };
    },
    downloadLatestFile() {
      if (!this.latestGeneratedFilePath) {
        this.$toast.warning(
          "مسیر فایل یافت نشد، لطفا از جدول پایین دانلود کنید.",
        );
        return;
      }

      this.isGenerating = true;

      this.$api
        .get(`admin/nfc/explorer?path=${this.latestGeneratedFilePath}`, {
          responseType: "blob",
        })
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("target", "_blank");
          const fileName =
            this.latestGeneratedFilePath.split("/").pop() || "nfc_files.zip";
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch(() => {
          this.$toast.error("خطا در دانلود فایل");
        })
        .finally(() => {
          this.isGenerating = false;
        });
    },
    handleMainButtonClick() {
      if (this.generationSuccess) {
        this.downloadLatestFile();

        // const tableSection = document.getElementById("files-table-section");
        // if (tableSection) {
        //   tableSection.scrollIntoView({ behavior: "smooth" });
        // }
      } else {
        this.generateBulkFiles();
      }
    },
    updatePreview() {
      if (!this.ribbon_can("nfc_add_reserve_qr_code")) return;
      this.previewLoading = true;
      this.qrSvgDataUrl = "";

      const rawPayload = {
        id: this.bulkOptions.id,
        ...this.getQrDesignParams(),
      };
      const payload = this.cleanPayload(rawPayload);
      this.$api
        .post("/admin/nfc/gen-image", payload)
        .then((res) => {
          const imgData = res.data.data || res.data;
          if (typeof imgData === "string" && imgData.includes("http")) {
            this.qrSvgDataUrl = imgData + "?t=" + new Date().getTime();
          } else if (typeof imgData === "string" && imgData.includes("<svg")) {
            this.qrSvgDataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(
              imgData,
            )}`;
          } else if (
            typeof imgData === "string" &&
            imgData.includes("data:image")
          ) {
            this.qrSvgDataUrl = imgData;
          } else {
            this.qrSvgDataUrl = `data:image/svg+xml;base64,${imgData}`;
          }
          this.hasChanges = false;
          this.$forceUpdate();
          // this.$toast.success("پیش‌نمایش با موفقیت به‌روزرسانی شد");
        })
        .catch(() => {
          this.$toast.error("خطا در دریافت پیش‌نمایش از سرور");
        })
        .finally(() => {
          this.previewLoading = false;
        });
    },

    generateBulkFiles() {
      if (this.bulkOptions.count < 1 || this.bulkOptions.count > 200) {
        this.$toast.error("تعداد باید بین 1 تا 200 باشد.");
        return;
      }
      if (!this.bulkOptions.front_image || !this.bulkOptions.back_image) {
        this.$toast.error("لطفا تصویر روی کارت و پشت کارت را انتخاب کنید.");
        return;
      }

      this.isGenerating = true;
      this.generationSuccess = false;
      this.latestGeneratedFilePath = "";
      const rawPayload = {
        count: this.bulkOptions.count,
        title: this.bulkOptions.title,
        front_image: this.bulkOptions.front_image,
        back_image: this.bulkOptions.back_image,
        orientation: this.bulkOptions.orientation,
        status: this.bulkOptions.status,
        ...this.getQrDesignParams(),
      };
      const payload = this.cleanPayload(rawPayload);

      // const payload = {
      //   count: this.bulkOptions.count,
      //   title: this.bulkOptions.title,
      //   front_image: this.bulkOptions.front_image,
      //   back_image: this.bulkOptions.back_image,
      //   orientation: this.bulkOptions.orientation,
      //   status: this.bulkOptions.status,
      //   ...this.getQrDesignParams(),
      // };

      this.$api
        .post("/admin/nfc/bulk-store", payload)
        .then((res) => {
          this.$toast.success(
            res.message || "بعد از چند دقیقه کیوآرها ساخته میشوند.",
          );
          this.generationSuccess = true;

          const responseData = res.data;
          this.latestGeneratedFilePath =
            responseData || responseData.name || "";
        })
        .catch(() => {
          this.$toast.error("خطا در ساخت گروهی کیوآرکدها");
        })
        .finally(() => {
          this.isGenerating = false;
        });
    },

    // POST nfc/explorer/files
    downloadFile(fileObj) {
      const filePath = fileObj.path || fileObj.name;

      const payload = {
        files: [filePath],
        directory: this.explorerPath,
      };

      this.$api
        .post("/admin/nfc/explorer/files", payload, { responseType: "blob" })
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileObj.name || "nfc_file.zip");
          link.setAttribute("target", "_blank");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch(() => {
          this.$toast.error("خطا در دانلود فایل");
        });
    },
  },
};
</script>
<style scoped>
.v-expansion-panel {
  border-radius: 15px !important;
  box-shadow: 8px 5px 10px #f49d1a;
}
.v-expansion-panel
  >>> .v-expansion-panel-header.v-expansion-panel-header--active {
  background-color: #001554 !important;
  border-radius: 15px !important;
  color: whitesmoke !important;
}
.v-expansion-panel
  >>> .v-expansion-panel-header.v-expansion-panel-header--active
  .v-icon {
  color: whitesmoke !important;
}
.submit-btn {
  border-radius: 10px;
  font-weight: bold;
}
.file-card {
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid transparent;
}
.file-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1) !important;
  border-color: #e0e0e0;
}
.selected-card {
  background-color: #f0f7ff !important;
  border: 1px solid #1976d2 !important;
}
.file-name {
  color: #333;
  font-weight: 500;
}
</style>
